import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { VolSurfaceToken } from '../../../model/currency_type.enum';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import usePersistentState from '../../use-persistent-state';
import { WidgetContext } from '../../../components/v2/widget-renderer/widget-provider';

interface OpenInterestProviderProps {
  children: ReactNode;
  providerKey?: string;
}

export interface OpenInterestDetail {
  und_token: string;
  acc_token: string;
  expiry: string;
  strike: string;
  cp: string;
  open_interest: number;
}

export interface OpenInterest {
  [key: string]: {
    [key: string]: {
      [key: string]: OpenInterestDetail;
    };
  };
}

interface OpenInterestResponse {
  id: string;
  result: {
    open_interest: OpenInterest;
  };
}

interface OpenInterestContextType {
  data: OpenInterest | undefined;
  timestamp: string;
  token: VolSurfaceToken;
  setToken: React.Dispatch<React.SetStateAction<VolSurfaceToken>>;
  hasDefaultToken: boolean;
  expiryList: string[];
  venueList: string[];
  isLoading: boolean;
  venue: string;
  expiry: string;
  setSelectedVenue: React.Dispatch<React.SetStateAction<string>>;
  setExpiry: React.Dispatch<React.SetStateAction<string>>;
}

const KEY_BASE = 'OPEN_INTEREST';

export const OpenInterestContext = createContext({} as OpenInterestContextType);

export const OpenInterestProvider = ({ children, providerKey }: OpenInterestProviderProps) => {
  const { token, setToken, hasDefaultToken } = useContext(WidgetContext);

  const KEY = `${providerKey}_${KEY_BASE}`;
  const STATE_PATH = [KEY, `${providerKey || 'DEFAULT'}`];

  const { subscribeToChannel, sendMessage, isConnected } = useContext(WebSocketContext);

  const [data, setData] = useState<OpenInterest | undefined>(undefined);
  const [timestamp, setTimestamp] = useState<string>(new Date().toISOString());
  const [expiryList, setExpiryList] = useState<string[]>([]);
  const [venueList, setVenueList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [venue, setSelectedVenue] = usePersistentState<string>(STATE_PATH, 'venue', 'deribit');
  const [expiry, setExpiry] = usePersistentState<string>(STATE_PATH, 'expiry', 'all');

  const RELOAD_INTERVAL = 60000;

  const handleData = useCallback(
    (response: OpenInterestResponse) => {
      if (!response || !response.result?.open_interest) {
        setIsLoading(false);
        return;
      }

      const openInterestData = response.result.open_interest;
      setData(openInterestData);

      const venues = Object.keys(openInterestData);
      setVenueList(venues);

      if (venues.length > 0 && openInterestData[venues[0]]?.[token]) {
        const expiries = Object.keys(openInterestData[venues[0]][token]);
        const currentExpiry = Array.isArray(expiry) ? expiry[0] : expiry;

        if (!expiries.includes(currentExpiry)) {
          setExpiry('all');
        }

        setExpiryList(expiries);
      }

      setIsLoading(false);
    },
    [token, expiry],
  );

  const handleGetOpenInterest = useCallback(() => {
    setIsLoading(true);
    const req = {
      id: KEY,
      method: 'option_open_interest',
      jsonrpc: '2.0',
      params: {
        option_markets: [
          {
            venue: 'deribit',
            token_pair: token,
          },
        ],
      },
    };

    sendMessage(JSON.stringify(req));

    subscribeToChannel(KEY, handleData);
    setTimestamp(new Date().toISOString());
  }, [sendMessage, token, KEY, subscribeToChannel, handleData]);

  useEffect(() => {
    if (isConnected) {
      handleGetOpenInterest();
      const interval = setInterval(handleGetOpenInterest, RELOAD_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [isConnected, handleGetOpenInterest]);

  useEffect(() => {
    if (data && venueList.length > 0 && data[venueList[0]]?.[token]) {
      const expiries = Object.keys(data[venueList[0]][token]);
      setExpiryList(expiries);
    }
  }, [data, token, venueList]);

  return (
    <OpenInterestContext.Provider
      value={{
        data,
        timestamp,
        token,
        setToken,
        hasDefaultToken,
        expiryList,
        venueList,
        isLoading,
        venue,
        setSelectedVenue,
        expiry,
        setExpiry,
      }}
    >
      {children}
    </OpenInterestContext.Provider>
  );
};
